import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: '#E8A547', bg: 'rgba(232,165,71,0.12)' },
  gestion: { label: 'En gestión', color: '#5B9BD5', bg: 'rgba(91,155,213,0.12)' },
  resuelto: { label: 'Resuelto', color: '#4A9B6F', bg: 'rgba(74,155,111,0.12)' },
}

const PRIORIDADES = {
  baja: { label: 'Baja', color: '#A8A49A' },
  media: { label: 'Media', color: '#E8C97A' },
  alta: { label: 'Alta', color: '#C0392B' },
}

const ORIGEN_LABELS = {
  oficina: '🏢 De oficina',
  comunes: '🏛 Espacios comunes',
  externo: '🌍 Externo',
}

// Panel detallado del reclamo: updates + archivos
function ReclamoDetalle({ reclamo, usuario, onUpdate, onClose }) {
  const [updates, setUpdates] = useState([])
  const [archivos, setArchivos] = useState([])
  const [nuevoUpdate, setNuevoUpdate] = useState('')
  const [nuevoEstado, setNuevoEstado] = useState(reclamo.estado)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => { fetchUpdates(); fetchArchivos() }, [reclamo.id])

  const fetchUpdates = async () => {
    const { data } = await supabase.from('reclamos_updates')
      .select('*, usuarios(nombre)').eq('reclamo_id', reclamo.id).order('created_at', { ascending: false })
    setUpdates(data || [])
  }

  const fetchArchivos = async () => {
    const { data } = await supabase.from('archivos_reclamos')
      .select('*').eq('reclamo_id', reclamo.id).order('created_at', { ascending: false })
    setArchivos(data || [])
  }

  const handleAddUpdate = async () => {
    if (!nuevoUpdate.trim() && nuevoEstado === reclamo.estado) return
    setLoading(true)

    await supabase.from('reclamos_updates').insert({
      reclamo_id: reclamo.id,
      usuario_id: usuario?.id,
      texto: nuevoUpdate.trim() || `Estado cambiado a "${ESTADOS[nuevoEstado].label}"`,
      estado_nuevo: nuevoEstado !== reclamo.estado ? nuevoEstado : null,
    })

    if (nuevoEstado !== reclamo.estado) {
      const updateData = { estado: nuevoEstado }
      if (nuevoEstado === 'resuelto') updateData.fecha_resolucion = new Date().toISOString()
      await supabase.from('reclamos').update(updateData).eq('id', reclamo.id)
    }

    setNuevoUpdate('')
    fetchUpdates()
    onUpdate()
    setLoading(false)
  }

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) { alert('Máximo 10 MB'); return }
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${reclamo.id}/${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage.from('documentos-reclamos').upload(path, file)
    if (upErr) { alert('Error: ' + upErr.message); setUploading(false); return }
    const { data: urlData } = supabase.storage.from('documentos-reclamos').getPublicUrl(path)
    await supabase.from('archivos_reclamos').insert({
      reclamo_id: reclamo.id, usuario_id: usuario?.id,
      nombre_archivo: file.name, url: urlData.publicUrl, tipo: file.type, tamano: file.size,
    })
    e.target.value = ''
    fetchArchivos()
    setUploading(false)
  }

  const handleDeleteArchivo = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.nombre_archivo}"?`)) return
    const path = a.url.split('/documentos-reclamos/')[1]
    if (path) await supabase.storage.from('documentos-reclamos').remove([path])
    await supabase.from('archivos_reclamos').delete().eq('id', a.id)
    fetchArchivos()
  }

  const formatFull = (ts) => new Date(ts).toLocaleString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  const formatSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`
  const iconArchivo = (t) => t?.includes('pdf') ? '📄' : t?.includes('image') ? '🖼️' : '📎'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 24px', overflowY: 'auto' }} onClick={onClose}>
      <div className="card fade-in" style={{ maxWidth: 680, width: '100%', borderColor: 'var(--gold)', borderWidth: 1.5, marginBottom: 40 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ ...{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, fontFamily: 'DM Mono, monospace' }, background: ESTADOS[reclamo.estado].bg, color: ESTADOS[reclamo.estado].color }}>
                ● {ESTADOS[reclamo.estado].label}
              </span>
              <span style={{ fontSize: 11, color: PRIORIDADES[reclamo.prioridad].color, fontFamily: 'DM Mono, monospace', letterSpacing: 0.5 }}>
                PRIORIDAD {PRIORIDADES[reclamo.prioridad].label.toUpperCase()}
              </span>
              {reclamo.categoria && <span className="badge badge-gray" style={{ fontSize: 10 }}>{reclamo.categoria}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 24, fontFamily: 'Cormorant Garamond, serif', fontWeight: 700, margin: 0 }}>{reclamo.titulo}</h2>
              {reclamo.origen === 'oficina' && reclamo.oficinas && (
                <span style={{ background: 'var(--gold)', color: '#0F0F0D', fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 13, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                  OF {reclamo.oficinas.numero}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 2 }}>
              {reclamo.origen === 'oficina' && reclamo.oficinas ? reclamo.oficinas.titular : ORIGEN_LABELS[reclamo.origen]}
              {reclamo.origen_descripcion && ` · ${reclamo.origen_descripcion}`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {ORIGEN_LABELS[reclamo.origen]}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 22, padding: 4 }}>✕</button>
        </div>

        {reclamo.descripcion && (
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 8 }}>DESCRIPCIÓN</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{reclamo.descripcion}</div>
          </div>
        )}

        {/* Archivos */}
        <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>
            ARCHIVOS · {archivos.length}
          </div>
          {archivos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {archivos.map(a => (
                <div key={a.id} style={{ background: 'var(--bg3)', borderRadius: 8, overflow: 'hidden' }}>
                  {a.tipo?.includes('image') ? (
                    <div style={{ position: 'relative' }}>
                      <a href={a.url} target="_blank" rel="noreferrer">
                        <img src={a.url} alt={a.nombre_archivo} style={{ width: '100%', maxHeight: 300, objectFit: 'contain', display: 'block', background: '#000' }} />
                      </a>
                      <button onClick={() => handleDeleteArchivo(a)} style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 13, borderRadius: 4, padding: '2px 7px', lineHeight: 1.5 }}>✕</button>
                      <div style={{ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{a.nombre_archivo} · {formatSize(a.tamano)}</div>
                        <a href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace' }}>ABRIR ↗</a>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
                      <span style={{ fontSize: 18 }}>{iconArchivo(a.tipo)}</span>
                      <a href={a.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, color: 'var(--text)', textDecoration: 'none', fontSize: 13 }}>
                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.nombre_archivo}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{formatSize(a.tamano)} · {new Date(a.created_at).toLocaleDateString('es-AR')}</div>
                      </a>
                      <button onClick={() => handleDeleteArchivo(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 14, padding: '2px 6px' }}>✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <label className="btn btn-ghost btn-sm" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
            {uploading ? 'Subiendo...' : '📎 Adjuntar archivo'}
            <input type="file" onChange={handleUpload} disabled={uploading}
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" style={{ display: 'none' }} />
          </label>
        </div>

        {/* Nuevo update */}
        <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>NUEVA ACTUALIZACIÓN</div>
          <textarea className="input" placeholder="Describí la actualización..." value={nuevoUpdate}
            onChange={e => setNuevoUpdate(e.target.value)} style={{ minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>CAMBIAR ESTADO A</label>
              <select className="input" value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)}>
                {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <button className="btn btn-gold btn-sm" onClick={handleAddUpdate}
              disabled={loading || (!nuevoUpdate.trim() && nuevoEstado === reclamo.estado)}
              style={{ alignSelf: 'flex-end' }}>
              Guardar
            </button>
          </div>
        </div>

        {/* Log de updates */}
        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>
            HISTORIAL · {updates.length} actualización{updates.length !== 1 ? 'es' : ''}
          </div>
          {updates.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: 20 }}>Sin actualizaciones todavía</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {updates.map(u => (
                <div key={u.id} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', borderLeft: u.estado_nuevo ? `3px solid ${ESTADOS[u.estado_nuevo].color}` : '3px solid var(--border2)' }}>
                  <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4, whiteSpace: 'pre-wrap' }}>{u.texto}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                    {formatFull(u.created_at)} · {u.usuarios?.nombre || 'Sistema'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  , document.body)
}

export default function Reclamos() {
  const { usuario } = useAuth()
  const [reclamos, setReclamos] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [edificios, setEdificios] = useState([])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [estadoFilter, setEstadoFilter] = useState('activos') // activos | resueltos | todos
  const [edificioFilter, setEdificioFilter] = useState('all')
  const [selectedReclamo, setSelectedReclamo] = useState(null)
  const [form, setForm] = useState({
    titulo: '', descripcion: '', categoria: '', prioridad: 'media',
    origen: 'oficina', oficina_id: '', origen_descripcion: '', edificio_id: ''
  })

  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificio_id

  useEffect(() => {
    fetchOficinas(); fetchReclamos()
    if (isAdmin) fetchEdificios()
  }, [])

  useEffect(() => { fetchReclamos() }, [estadoFilter, edificioFilter])

  const fetchEdificios = async () => {
    const { data } = await supabase.from('edificios').select('*')
    setEdificios(data || [])
  }

  const fetchOficinas = async () => {
    let q = supabase.from('oficinas').select('*, edificios(nombre)').eq('activa', true).order('numero')
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setOficinas(data || [])
  }

  const fetchReclamos = async () => {
    setLoading(true)
    let q = supabase
      .from('reclamos')
      .select('*, oficinas(numero, titular), edificios(nombre), usuarios(nombre)')
      .order('created_at', { ascending: false })
    if (edificioId) q = q.eq('edificio_id', edificioId)
    else if (edificioFilter !== 'all') q = q.eq('edificio_id', edificioFilter)
    if (estadoFilter === 'activos') q = q.in('estado', ['pendiente', 'gestion'])
    else if (estadoFilter === 'resueltos') q = q.eq('estado', 'resuelto')
    const { data } = await q
    setReclamos(data || [])
    setLoading(false)
  }

  const handleSubmit = async () => {
    if (!form.titulo.trim()) return
    if (form.origen === 'oficina' && !form.oficina_id) return
    setLoading(true)

    let edificioFinal = edificioId
    let oficina = null
    if (form.origen === 'oficina') {
      oficina = oficinas.find(o => o.id === form.oficina_id)
      edificioFinal = oficina.edificio_id
    } else if (isAdmin) {
      edificioFinal = form.edificio_id
    }

    const { data: newReclamo, error } = await supabase.from('reclamos').insert({
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      categoria: form.categoria.trim() || null,
      prioridad: form.prioridad,
      origen: form.origen,
      oficina_id: form.origen === 'oficina' ? form.oficina_id : null,
      origen_descripcion: form.origen !== 'oficina' ? (form.origen_descripcion.trim() || null) : null,
      edificio_id: edificioFinal,
      usuario_id: usuario?.id,
    }).select().single()

    if (!error && newReclamo) {
      // Email a admins
      const edificio = edificios.find(e => e.id === edificioFinal) || { nombre: '' }
      const edificioNombre = oficina?.edificios?.nombre || edificio.nombre
      for (const email of ['francisco@grupofuegos.com', 'galanbrokerinmobiliario@gmail.com']) {
        fetch('/api/send-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'reclamo',
            destinatario_email: email,
            oficina: oficina?.numero || ORIGEN_LABELS[form.origen],
            oficina_titular: oficina?.titular || '',
            edificio: edificioNombre,
            datos: {
              titulo: form.titulo, descripcion: form.descripcion,
              categoria: form.categoria, prioridad: form.prioridad,
              origen: ORIGEN_LABELS[form.origen],
              registrado_por: usuario?.nombre,
            }
          })
        }).catch(e => console.error(e))
      }

      setSuccess('Reclamo registrado')
      setTimeout(() => setSuccess(null), 3000)
      setForm({ titulo: '', descripcion: '', categoria: '', prioridad: 'media', origen: 'oficina', oficina_id: '', origen_descripcion: '', edificio_id: '' })
      setShowForm(false)
      fetchReclamos()
    }
    setLoading(false)
  }

  const formatDate = (ts) => new Date(ts).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })

  return (
    <div className="fade-in">
      {success && (
        <div style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--green)', color: 'white', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', animation: 'slideDown 0.3s ease' }}>
          ✓ {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Reclamos</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>
            {reclamos.length} reclamo{reclamos.length !== 1 ? 's' : ''} {estadoFilter === 'activos' ? 'activos' : estadoFilter === 'resueltos' ? 'resueltos' : 'totales'}
          </p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancelar' : '+ Nuevo reclamo'}
        </button>
      </div>

      {showForm && (
        <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--gold)', borderWidth: 1.5 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 20 }}>NUEVO RECLAMO</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="mobile-grid-1">

            {/* Origen */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>ORIGEN DEL RECLAMO</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(ORIGEN_LABELS).map(([key, label]) => (
                  <button key={key} type="button"
                    className={`btn btn-sm ${form.origen === key ? 'btn-gold' : 'btn-ghost'}`}
                    onClick={() => setForm(f => ({ ...f, origen: key, oficina_id: '', origen_descripcion: '' }))}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>TÍTULO</label>
              <input className="input" placeholder="Ej: Filtración en pasillo del 3er piso" value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} />
            </div>

            {form.origen === 'oficina' && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>OFICINA</label>
                <select className="input" value={form.oficina_id} onChange={e => setForm(f => ({ ...f, oficina_id: e.target.value }))}>
                  <option value="">Seleccioná oficina...</option>
                  {oficinas.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.titular} {isAdmin ? `(${o.edificios?.nombre})` : ''}</option>)}
                </select>
              </div>
            )}

            {form.origen !== 'oficina' && isAdmin && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EDIFICIO</label>
                <select className="input" value={form.edificio_id} onChange={e => setForm(f => ({ ...f, edificio_id: e.target.value }))}>
                  <option value="">Seleccioná edificio...</option>
                  {edificios.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
            )}

            {form.origen !== 'oficina' && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
                  {form.origen === 'comunes' ? 'UBICACIÓN' : 'QUIÉN'} (opcional)
                </label>
                <input className="input"
                  placeholder={form.origen === 'comunes' ? 'Ej: Cochera, hall, escalera' : 'Ej: Vecino del edificio de al lado'}
                  value={form.origen_descripcion}
                  onChange={e => setForm(f => ({ ...f, origen_descripcion: e.target.value }))} />
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>CATEGORÍA</label>
              <input className="input" placeholder="Ej: Limpieza, Plomería, Ruidos..." value={form.categoria}
                onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>PRIORIDAD</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(PRIORIDADES).map(([key, val]) => (
                  <button key={key} type="button"
                    className={`btn btn-sm ${form.prioridad === key ? 'btn-gold' : 'btn-ghost'}`}
                    style={{ flex: 1 }}
                    onClick={() => setForm(f => ({ ...f, prioridad: key }))}>
                    {val.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>DESCRIPCIÓN</label>
              <textarea className="input" placeholder="Describí el reclamo en detalle..." value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                style={{ minHeight: 80, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            <button className="btn btn-gold btn-sm" onClick={handleSubmit}
              disabled={loading || !form.titulo.trim() || (form.origen === 'oficina' && !form.oficina_id) || (form.origen !== 'oficina' && isAdmin && !form.edificio_id)}>
              Registrar reclamo
            </button>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${estadoFilter === 'activos' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEstadoFilter('activos')}>Activos</button>
          <button className={`btn btn-sm ${estadoFilter === 'resueltos' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEstadoFilter('resueltos')}>Resueltos</button>
          <button className={`btn btn-sm ${estadoFilter === 'todos' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEstadoFilter('todos')}>Todos</button>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`btn btn-sm ${edificioFilter === 'all' ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter('all')}>Todos los edificios</button>
            {edificios.map(e => (
              <button key={e.id} className={`btn btn-sm ${edificioFilter === e.id ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setEdificioFilter(e.id)}>
                {e.nombre.replace('Essence ', '')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>Cargando...</div>
      ) : reclamos.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>No hay reclamos {estadoFilter === 'activos' ? 'activos' : estadoFilter === 'resueltos' ? 'resueltos' : ''}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reclamos.map(r => (
            <div key={r.id} className="card" style={{ padding: '16px 20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
              onClick={() => setSelectedReclamo(r)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: PRIORIDADES[r.prioridad].color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 600, fontFamily: 'DM Mono, monospace', background: ESTADOS[r.estado].bg, color: ESTADOS[r.estado].color }}>
                      ● {ESTADOS[r.estado].label}
                    </span>
                    {r.categoria && <span className="badge badge-gray" style={{ fontSize: 10 }}>{r.categoria}</span>}
                    <span style={{ fontSize: 10, color: PRIORIDADES[r.prioridad].color, fontFamily: 'DM Mono, monospace', letterSpacing: 0.5 }}>
                      {PRIORIDADES[r.prioridad].label.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{r.titulo}</span>
                    {r.origen === 'oficina' && r.oficinas && (
                      <span style={{ background: 'var(--gold)', color: '#0F0F0D', fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 5, whiteSpace: 'nowrap' }}>
                        OF {r.oficinas.numero}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {r.origen === 'oficina' && r.oficinas ? r.oficinas.titular : ORIGEN_LABELS[r.origen]}
                    {r.origen_descripcion && ` · ${r.origen_descripcion}`}
                    {isAdmin && r.edificios && <span style={{ color: 'var(--gold)', marginLeft: 6 }}>· {r.edificios.nombre.replace('Essence ', '')}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>{formatDate(r.created_at)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{r.usuarios?.nombre}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedReclamo && (
        <ReclamoDetalle reclamo={selectedReclamo} usuario={usuario}
          onUpdate={() => { fetchReclamos(); supabase.from('reclamos').select('*, oficinas(numero, titular), edificios(nombre)').eq('id', selectedReclamo.id).single().then(({ data }) => data && setSelectedReclamo(data)) }}
          onClose={() => setSelectedReclamo(null)} />
      )}
    </div>
  )
}
